import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { auth } from '@/lib/firebase';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  type: 'step_group' | 'modal';
  order: number;
  required: boolean;
  component?: string; // Component name for modal type steps
  meta?: {
    estimatedTime?: number;
    icon?: string;
    weight?: number;
    ctaText?: string;
    config?: {
      scopeStatement?: string;
      hasExistingSystem?: boolean;
      hasExclusions?: boolean;
      [key: string]: any;
    };
  };
  substeps?: OnboardingStep[];
  completed?: boolean;
  path?: string; // Full path including parent steps (e.g., 'onboarding:welcome')
}

export interface OnboardingFlow {
  version: string;
  updatedAt: string;
  flow: {
    settings: {
      allowStepSkipping: boolean;
      autoSave: boolean;
      completionRequired: boolean;
      enableReminders: boolean;
      inactivityTimeout: number;
      lockCompletedSteps: boolean;
      showProgressBar: boolean;
      showStepNumbers: boolean;
    };
    steps: OnboardingStep[];
  };
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

export const getOnboardingConfig = async (): Promise<OnboardingFlow | null> => {
  try {
    const response = await deiterateAPI.GET<ApiResponse<OnboardingFlow>>(
      '/v1/onboarding/config',
      { backend: 'thalassa' }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching onboarding config:', error);
    return null;
  }
};

export const subscribeToOnboardingConfig = (
  callback: (config: OnboardingFlow | null) => void
) => {
  const docRef = doc(db, 'onboarding', 'global_config');
  
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data() as OnboardingFlow);
    } else {
      callback(null);
    }
  });
};

/**
 * Gets the full path of a step including its parent steps (e.g., 'onboarding:welcome')
 */
export const getStepPath = (steps: OnboardingStep[], stepId: string, parentPath: string = ''): string | null => {
  for (const step of steps) {
    const currentPath = parentPath ? `${parentPath}:${step.id}` : step.id;
    
    if (step.id === stepId) {
      return currentPath;
    }
    
    if (step.substeps) {
      const found = getStepPath(step.substeps, stepId, step.id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Updates a user's onboarding progress by step ID
 * @param userId - The user's ID
 * @param stepId - The ID of the step to update
 * @param completed - Whether the step is completed
 * @param allSteps - All available steps to build the full path
 * @returns Promise<boolean> - Whether the update was successful
 */
export const updateUserOnboardingProgress = async (
  userId: string, 
  stepId: string, 
  completed: boolean,
  allSteps?: OnboardingStep[]
) => {
  try {
    // Get the full path for the step (e.g., 'onboarding:welcome')
    let stepPath = stepId;
    if (allSteps) {
      stepPath = getStepPath(allSteps, stepId) || stepId;
    }
    
    await deiterateAPI.PUT(
      `/v1/onboarding/progress/${userId}`,
      {
        backend: 'thalassa',
        body: JSON.stringify({
          stepId,
          completed,
          stepPath
        })
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error updating user onboarding progress:', error);
    return false;
  }
};

export const getUserOnboardingProgress = async (userId: string): Promise<string[]> => {
  try {
    const response = await deiterateAPI.GET<ApiResponse<{ completedSteps: string[] }>>(
      `/v1/onboarding/progress/${userId}`,
      { backend: 'thalassa' }
    );
    return response.data.completedSteps || [];
  } catch (error) {
    console.error('Error fetching user progress:', error);
    return [];
  }
};

/**
 * Checks if a step is completed by its ID or full path
 * Handles both direct matches and parent:child format
 */
export const isStepCompleted = (completedSteps: string[], stepId: string): boolean => {
  // Check for direct match or any path that ends with the stepId
  return completedSteps.some(step => {
    // Direct match
    if (step === stepId) return true;
    
    // Check if this is a parent:child relationship
    const parts = step.split(':');
    return parts[parts.length - 1] === stepId;
  });
};

export const subscribeToUserOnboardingProgress = (
  userId: string,
  callback: (completedSteps: string[]) => void
) => {
  const userRef = doc(db, 'users', userId);
  
  return onSnapshot(userRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      callback(data.completedOnboardingSteps || []);
    } else {
      callback([]);
    }
  });
};
